import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Save, X, Loader2 } from 'lucide-react';
import { fetchCrystals, createCrystal, updateCrystal, deleteCrystal, resolveImageUrl, formatApiError } from '../../services/api';
import ImageUploader from './ImageUploader';

const emptyCrystal = { name: '', category: '', description: '', image: '', benefits: [], price: null };

const CrystalManager = () => {
  const [crystals, setCrystals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCrystal);
  const [benefitsText, setBenefitsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCrystals();
      setCrystals(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing('new');
    setForm(emptyCrystal);
    setBenefitsText('');
    setError('');
  };

  const startEdit = (crystal) => {
    setEditing(crystal.id);
    setForm({ ...crystal });
    setBenefitsText((crystal.benefits || []).join(', '));
    setError('');
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyCrystal);
    setBenefitsText('');
    setError('');
  };

  const save = async () => {
    if (!form.name || !form.category || !form.description || !form.image) {
      setError('Name, Category, Description, and Image are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const benefits = benefitsText.split(',').map(s => s.trim()).filter(Boolean);
      const payload = {
        name: form.name,
        category: form.category,
        description: form.description,
        image: form.image,
        benefits,
        price: form.price ? parseInt(form.price, 10) : null,
      };
      if (editing === 'new') await createCrystal(payload);
      else await updateCrystal(editing, payload);
      await load();
      cancel();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this crystal?')) return;
    try {
      await deleteCrystal(id);
      await load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>Crystals</h2>
          <p className="text-amber-100/60 text-sm mt-1">{crystals.length} crystal{crystals.length !== 1 ? 's' : ''}</p>
        </div>
        {!editing && (
          <Button onClick={startCreate} className="btn-gold" data-testid="new-crystal-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Crystal
          </Button>
        )}
      </div>

      {editing && (
        <div className="mb-8 p-6 bg-emerald-950/40 border border-amber-600/30 rounded-lg space-y-4">
          <h3 className="text-xl font-semibold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>
            {editing === 'new' ? 'Add New Crystal' : 'Edit Crystal'}
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Rose Quartz Crystal"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                data-testid="crystal-name-input"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Category *</Label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. Love & Relationships"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                data-testid="crystal-category-input"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Price (₹, optional)</Label>
              <Input
                type="number"
                value={form.price || ''}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="e.g. 500"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Benefits (comma-separated)</Label>
              <Input
                value={benefitsText}
                onChange={(e) => setBenefitsText(e.target.value)}
                placeholder="Attracts love, Emotional healing, Self-acceptance"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                data-testid="crystal-benefits-input"
              />
            </div>
          </div>

          <ImageUploader
            value={form.image}
            onChange={(url) => setForm({ ...form, image: url })}
            label="Crystal Image *"
          />

          <div>
            <Label className="text-amber-300 text-xs uppercase tracking-wider">Description *</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Stone of unconditional love and infinite peace..."
              rows={4}
              className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              data-testid="crystal-description-input"
            />
          </div>

          {error && <div className="p-3 rounded bg-red-950/60 border border-red-500/40 text-red-300 text-sm">{error}</div>}

          <div className="flex gap-3">
            <Button onClick={save} disabled={saving} className="btn-gold" data-testid="crystal-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save Crystal'}
            </Button>
            <Button onClick={cancel} className="btn-mystical">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-amber-100/60 text-center py-12">Loading crystals...</p>
      ) : crystals.length === 0 ? (
        <p className="text-amber-100/60 text-center py-12">No crystals yet. Click "Add Crystal" to create one.</p>
      ) : (
        <div className="space-y-3">
          {crystals.map((crystal) => (
            <div
              key={crystal.id}
              className="flex items-center gap-4 p-4 bg-emerald-950/30 border border-amber-600/20 rounded-lg hover:border-amber-500/50 transition"
              data-testid={`crystal-row-${crystal.id}`}
            >
              <img
                src={resolveImageUrl(crystal.image)}
                alt={crystal.name}
                className="w-20 h-20 object-cover rounded border border-amber-600/30 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-amber-200 font-semibold truncate" style={{ fontFamily: 'Cinzel, serif' }}>{crystal.name}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className="bg-emerald-800/80 text-amber-300 border border-amber-500/30 text-xs">{crystal.category}</Badge>
                  {crystal.price && <span className="text-xs text-amber-300">₹{crystal.price}</span>}
                </div>
                <p className="text-sm text-amber-100/70 line-clamp-1 mt-1">{crystal.description}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => startEdit(crystal)} className="btn-mystical" data-testid={`edit-crystal-${crystal.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" onClick={() => handleDelete(crystal.id)} className="bg-red-900/60 text-red-300 hover:bg-red-900 border border-red-500/30" data-testid={`delete-crystal-${crystal.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CrystalManager;
